/// <amd-module name="Repos/Views/Import/Components/ImportProgress/ImportProgress" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";

import { Button } from "VSSUI/Button";
import { Header, TitleSize } from "VSSUI/Header";
import { Image } from "VSSUI/Image";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { ProgressBar } from "VSSUI/ProgressBar";
import { Spinner, SpinnerOrientation } from "VSSUI/Spinner";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssLocationService } from "VSS/Platform/Location";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";

import { getTFSProject } from "Tfs/Platform/Page";

import { GitAsyncOperationStatus, GitImportRequest, GitRepository } from "Repos/Common/Contracts/Git";
import { IVCViewModelDataProviderService } from "Repos/Common/VCViewModelDataProviderService";

import * as Resources from "Repos/Views/Import/Resources";
import { getGitClient } from "Repos/Views/Import/RestClient/Git";

import { ImportProgressProps } from "./ImportProgress.Props";

export class ImportProgress extends VssComponent<ImportProgressProps> {
    private errorMessage: ObservableValue<string | undefined>;
    private helpLink: string;
    private imageFailed: string;
    private imageInProgress: string;
    private importSourceUrl: string;
    private imageSucceeded: string;
    private navigateTimeout = 0;
    private pollingInterval = 0;
    private projectId: string;
    private repository: GitRepository;
    private repositoryPage: string;
    private status: ObservableValue<GitAsyncOperationStatus>;
    private stepCount: number;
    private stepNumber: ObservableValue<number>;

    constructor(props: ImportProgressProps, context: IVssComponentContext) {
        super(props, context);

        const { importRequest } = props;
        const vcViewModelDataProviderService = context.pageContext.getService<IVCViewModelDataProviderService>("IVCViewModelDataProviderService");
        const vcViewModel = vcViewModelDataProviderService.getViewModel();

        // Ensure we are in the right state to show this page.
        if (!vcViewModel.gitRepository) {
            throw new Error(""); // @TODO: What do we do if there is no repository available?
        }

        this.repository = vcViewModel.gitRepository;

        // Compute some of the fields based on the type of import.
        this.helpLink = importRequest.parameters.gitSource
            ? "https://go.microsoft.com/fwlink/?linkid=836846"
            : "https://go.microsoft.com/fwlink/?linkid=839462";

        this.importSourceUrl =
            (importRequest.parameters.tfvcSource && importRequest.parameters.tfvcSource.path) ||
            (importRequest.parameters.gitSource && importRequest.parameters.gitSource.url);

        // Get the location for the status images we will use.
        const locationService = context.pageContext.getService<IVssLocationService>("IVssLocationService");
        this.imageFailed = locationService.getAssetLocation("ms.vss-code-web/import-content/importFailed.png");
        this.imageInProgress = locationService.getAssetLocation("ms.vss-code-web/import-content/importInProgress.png");
        this.imageSucceeded = locationService.getAssetLocation("ms.vss-code-web/import-content/importSucceeded.png");

        // Get the project name we display in the title.
        const project = getTFSProject(context.pageContext);
        if (project) {
            this.projectId = project.id;
            this.repositoryPage = locationService.routeUrl("ms.vss-code-web.files-route-git", {
                project: project.name,
                "vc.GitRepositoryName": this.repository.name
            });
        }

        this.errorMessage = new ObservableValue<string | undefined>(importRequest.detailedStatus && importRequest.detailedStatus.errorMessage);

        this.status = new ObservableValue<GitAsyncOperationStatus>(importRequest.status);
        this.stepCount = importRequest.detailedStatus.allSteps.length;
        this.stepNumber = new ObservableValue<number>(importRequest.detailedStatus.currentStep);
    }

    public render() {
        return (
            <Observer status={this.status} stepCount={this.stepCount} stepNumber={this.stepNumber}>
                {(props: { status: GitAsyncOperationStatus; stepCount: number; stepNumber: number }) => {
                    // Start a navigation timeout when the import completes.
                    if (props.status === GitAsyncOperationStatus.Completed && !this.navigateTimeout) {
                        this.navigateTimeout = window.setTimeout(() => {
                            this.navigateTimeout = 0;
                            FastPageSwitch(this.context.pageContext, this.repositoryPage, false);
                        }, 5000);
                    }

                    return (
                        <Page className="repo-import-page flex-grow">
                            <div className="repo-import-container flex-column flex-center rhythm-vertical-16 text-center">
                                {props.status === GitAsyncOperationStatus.Completed ? (
                                    <>
                                        <Header title={Resources.ImportSucceededHeader} titleSize={TitleSize.Large} />
                                        <Image className="repo-import-image-succeeded" src={this.imageSucceeded} />
                                        <span>{format(Resources.ImportSucceededContent, this.importSourceUrl)}</span>
                                        <FormatComponent format={Resources.ImportSucceededFooter}>
                                            <FPSLink href={this.repositoryPage}>{Resources.ImportSucceededLink}</FPSLink>
                                        </FormatComponent>
                                    </>
                                ) : props.status === GitAsyncOperationStatus.Queued || props.status === GitAsyncOperationStatus.InProgress ? (
                                    <>
                                        <Header title={Resources.ImportInProgressHeader} titleSize={TitleSize.Large} />
                                        <Image className="repo-import-image-inprogress" src={this.imageInProgress} />
                                        <ProgressBar className="flex-self-stretch" currentValue={props.stepNumber} maxValue={props.stepCount} />
                                        <Spinner className="font-size-ms" label={Resources.ProcessingRequest} orientation={SpinnerOrientation.row} />
                                        <span>{format(Resources.ImportInProgressContent, this.importSourceUrl)}</span>
                                        <span>{Resources.ImportInProgressFooter}</span>
                                    </>
                                ) : props.status === GitAsyncOperationStatus.Failed ? (
                                    <>
                                        <Header title={Resources.ImportFailedHeader} titleSize={TitleSize.Large} />
                                        <Image className="repo-import-image-succeeded" src={this.imageFailed} />
                                        <span>{format(Resources.ImportFailedContent, this.importSourceUrl, this.errorMessage.value)}</span>
                                        <Link href={this.helpLink} target="_blank">
                                            {Resources.LearnMoreError}
                                        </Link>
                                        <Button
                                            onClick={this.onCancel}
                                            text={Resources.Cancel}
                                            tooltipProps={{ text: Resources.ImportFailedFooter }}
                                        />
                                    </>
                                ) : null}
                            </div>
                        </Page>
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount() {
        super.componentDidMount();

        const pageContext = this.context.pageContext;

        this.pollingInterval = window.setInterval(() => {
            const gitClient = getGitClient(pageContext);

            // Get the latest status of this import request.
            gitClient.getImportRequest(this.projectId, this.repository.id, this.props.importRequest.importRequestId).then(importOperation => {
                this.errorMessage.value = importOperation.detailedStatus.errorMessage;
                this.status.value = importOperation.status;
                this.stepNumber.value = importOperation.detailedStatus.currentStep;
            });
        }, 10000);
    }

    public componentWillUnmount() {
        super.componentWillUnmount();

        if (this.navigateTimeout) {
            window.clearTimeout(this.navigateTimeout);
        }

        if (this.pollingInterval) {
            window.clearInterval(this.pollingInterval);
        }
    }

    private onCancel = () => {
        const gitClient = getGitClient(this.context.pageContext);

        // Marke this import request as abandoned.
        this.props.importRequest.status = GitAsyncOperationStatus.Abandoned;
        gitClient
            .updateImportRequest(
                { status: GitAsyncOperationStatus.Abandoned } as GitImportRequest,
                this.projectId,
                this.repository.id,
                this.props.importRequest.importRequestId
            )
            .then(() => {
                FastPageSwitch(this.context.pageContext, this.repositoryPage, false);
            });
    };
}
